import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wallet, Gift, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

export default function LoyaltyCreditsCard({ subscriptionPlan, subscriptionStatus, pricingLocked }) {
  const [loyalty, setLoyalty] = useState(null);
  const [credits, setCredits] = useState(null);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [loy, cre, pks] = await Promise.all([
        axios.get(`${API}/me/loyalty`).catch(() => ({ data: null })),
        axios.get(`${API}/me/credits`).catch(() => ({ data: null })),
        axios.get(`${API}/credit-packs`).catch(() => ({ data: [] })),
      ]);
      setLoyalty(loy.data);
      setCredits(cre.data);
      setPacks(pks.data || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const redeem = async (points) => {
    setBusy(`redeem_${points}`);
    try {
      await axios.post(`${API}/me/loyalty/redeem`, { points });
      toast.success("Points redeemed for ride credits!");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Redeem failed");
    } finally {
      setBusy(null);
    }
  };

  const buyPack = async (pack_id) => {
    setBusy(`buy_${pack_id}`);
    try {
      const { data } = await axios.post(`${API}/me/credits/buy`, {
        pack_id,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Checkout failed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pack purchase failed");
      setBusy(null);
    }
  };

  if (!loyalty || !credits) return null;

  const points = loyalty.points || 0;
  const next = loyalty.next_tier;
  const progressPct = next ? Math.min(100, ((points - (loyalty.tiers.find(t => t.points < next.points)?.points || 0)) / (next.points - (loyalty.tiers.find(t => t.points < next.points)?.points || 0))) * 100) : 100;

  return (
    <div className="space-y-3" data-testid="loyalty-credits-card">
      {/* Wallet balance */}
      <Card className="bg-gradient-to-br from-amber-500/15 to-transparent border-amber-500/30">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30">
                <Wallet className="w-4 h-4 text-amber-400" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Wallet Credits</span>
            </div>
            {pricingLocked && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 font-bold px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                <Lock className="w-3 h-3" /> Price Locked
              </span>
            )}
          </div>
          <p className="text-3xl font-black text-amber-300" style={{ fontFamily: "Outfit, system-ui" }}>
            ${credits.balance_dollars.toFixed(2)}
          </p>
          <p className="text-xs text-white/50 mt-1">Use on any ride or delivery — never expires</p>

          <div className="grid grid-cols-2 gap-2 mt-4">
            {packs.map(p => (
              <Button
                key={p.id}
                size="sm"
                onClick={() => buyPack(p.id)}
                disabled={!!busy}
                data-testid={`account-buy-${p.id}`}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg disabled:opacity-50 text-xs"
              >
                {busy === `buy_${p.id}` ? "Loading…" : `+ $${(p.credit_cents/100).toFixed(0)} for $${(p.price_cents/100).toFixed(0)}`}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Loyalty points */}
      {(() => { const isActivePro = subscriptionPlan && (subscriptionStatus || "").toLowerCase() === "active"; return (
      <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30">
                <Sparkles className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Loyalty Points</span>
            </div>
            {isActivePro && (
              <span className="text-[10px] uppercase tracking-wider text-[#4D88FF] font-bold px-2 py-1 rounded-full bg-[#0047FF]/10 border border-[#0047FF]/30" data-testid="loyalty-pro-pill">
                1.5× member boost
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-black text-emerald-300" style={{ fontFamily: "Outfit, system-ui" }}>
              {points.toLocaleString()}
            </p>
            <span className="text-sm text-white/50">points</span>
          </div>

          {isActivePro && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-gradient-to-r from-[#0047FF]/15 to-emerald-500/10 border border-[#0047FF]/30 flex items-center gap-2" data-testid="loyalty-pro-callout">
              <Sparkles className="w-4 h-4 text-[#4D88FF] shrink-0" />
              <p className="text-[11px] text-white/90 leading-snug">
                <span className="font-bold text-[#4D88FF]">Earning 1.5×</span> on every completed trip as a Link Pro member.
              </p>
            </div>
          )}

          {/* Progress to next tier */}
          {next ? (
            <div className="mt-3">
              <div className="flex justify-between text-[11px] text-white/60 mb-1.5">
                <span>{loyalty.points_to_next} more for {next.label}</span>
                <span className="font-bold">{points} / {next.points}</span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                  style={{ width: `${Math.min(100, (points / next.points) * 100)}%` }}
                />
              </div>
            </div>
          ) : (
            <p className="text-xs text-emerald-300 mt-2">All reward tiers unlocked!</p>
          )}

          {/* Reward tier buttons */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            {loyalty.tiers.map(t => {
              const canRedeem = points >= t.points;
              return (
                <button
                  key={t.points}
                  onClick={() => canRedeem && redeem(t.points)}
                  disabled={!canRedeem || !!busy}
                  data-testid={`redeem-${t.points}`}
                  className={`py-2 px-2 rounded-lg border text-center transition-colors ${
                    canRedeem
                      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 cursor-pointer"
                      : "bg-white/5 border-white/10 text-white/40 cursor-not-allowed"
                  }`}
                >
                  <Gift className="w-3.5 h-3.5 mx-auto mb-1" />
                  <p className="text-[10px] uppercase tracking-wider font-bold">{t.points} pts</p>
                  <p className="text-[10px] mt-0.5 opacity-75">${(t.credit_cents/100).toFixed(0)} credit</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      ); })()}
    </div>
  );
}
